import React from 'react';
import './Milestones.css';

type MilestoneStatus = 'Выполнено' | 'В процессе' | 'Планируется';

const Milestones: React.FC = () => {
  const milestones: Array<{
    number: number;
    title: string;
    description: string;
    tasks: string[];
    status: MilestoneStatus;
  }> = [
    {
      number: 0,
      title: 'Подготовка',
      description: 'Формирование идеи и команды',
      tasks: ['Первоначальный план идеи', 'Определение MVP и ключевых фич', 'Покупка домена'],
      status: 'Выполнено'
    },
    {
      number: 1,
      title: 'Визитка (Landing Page)',
      description: 'Создание celestix.org',
      tasks: ['Концепт, дизайн, описание', 'Форма заявок на бета-тест', 'Сбор email', 'Развертка инфраструктуры (сервер, установка Gitea)', 'Развертка визитки'],
      status: 'В процессе'
    },
    {
      number: 2,
      title: 'Концепт и валидация',
      description: 'Описание + проверка рынка',
      tasks: ['Опрос студентов (100+)', 'Интервью с 10+ вузами', 'Метрики: интерес >70%'],
      status: 'Планируется'
    },
    {
      number: 3,
      title: 'Юридическая основа',
      description: 'Добавить перед инвесторами',
      tasks: ['Регистрация юрлица (ИП/ООО)', 'GDPR/152-ФЗ compliance', 'Политика конфиденциальности'],
      status: 'Планируется'
    },
    {
      number: 4,
      title: 'Обсуждение с инвесторами',
      description: 'Pitch deck + переговоры',
      tasks: ['Демо визитки, метрики валидации', 'Финансовый план', 'Поиск грантов (Фонд Бортника, Сколково)'],
      status: 'Планируется'
    },
    {
      number: 5,
      title: 'Архитектура системы',
      description: 'Техническая основа',
      tasks: ['Диаграммы (UML/ERD)', 'Выбор стека (Python/Django + React Native?)', 'Облако (Yandex Cloud)'],
      status: 'Планируется'
    },
    {
      number: 6,
      title: 'Дизайн и прототип',
      description: 'UI/UX',
      tasks: ['Figma прототипы, user flows', 'A/B тесты на 20+ студентах'],
      status: 'Планируется'
    },
    {
      number: 7,
      title: 'Backend разработка',
      description: 'Серверная часть',
      tasks: ['API (REST/GraphQL)', 'БД (PostgreSQL)', 'Интеграции (музыка, шифрование)'],
      status: 'Планируется'
    },
    {
      number: 8,
      title: 'Frontend + Мобильное',
      description: 'Клиентская часть',
      tasks: ['Web app + мобильная аутентификация', 'PWA для старта'],
      status: 'Планируется'
    },
    {
      number: 9,
      title: 'Альфа/Бета тестирование',
      description: 'Добавить',
      tasks: ['Закрытое тестирование (50 студентов)', 'Фикс багов', 'Метрики retention >60%'],
      status: 'Планируется'
    },
    {
      number: 10,
      title: 'Pre-release (MVP)',
      description: 'Запуск',
      tasks: ['Soft launch в 1-2 вузах', 'Мониторинг', 'Итерации по фидбеку'],
      status: 'Планируется'
    }
  ];

  const [expandedDone, setExpandedDone] = React.useState<number | null>(null);

  const completedMilestones = milestones.filter(m => m.status === 'Выполнено');
  const inProgressMilestone = milestones.find(m => m.status === 'В процессе');
  const plannedMilestones = milestones.filter(m => m.status === 'Планируется');

  const renderMilestoneCard = (milestone: typeof milestones[0], variant: 'badge' | 'current' | 'planned') => {
    if (variant === 'badge') {
      const isExpanded = expandedDone === milestone.number;
      return (
        <div 
          key={milestone.number} 
          className={`milestone-badge ${isExpanded ? 'milestone-badge--expanded' : ''}`}
          onClick={() => setExpandedDone(isExpanded ? null : milestone.number)}
        >
          <div className="milestone-badge__compact">
            <span className="milestone-badge__number">{milestone.number}</span>
            <span className="milestone-badge__title">{milestone.title}</span>
          </div>
          {isExpanded && (
            <div className="milestone-badge__details">
              <p className="milestone-badge__description">{milestone.description}</p>
              <div className="milestone-badge__tasks">
                <h4>Ключевые задачи:</h4>
                <ul>
                  {milestone.tasks.map((task, idx) => (
                    <li key={idx}>{task}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      );
    }

    const cardClass = variant === 'current' ? 'milestone-card card milestone-card--current' : 'milestone-card card milestone-card--planned';
    
    return (
      <div key={milestone.number} className={cardClass}>
        <div className="milestone-card__header">
          <div className="milestone-card__number">{milestone.number}</div>
          <div className="milestone-card__header-content">
            {variant === 'current' ? (
              <>
                <div>
                  <h3 className="milestone-card__title">{milestone.title}</h3>
                  <p className="milestone-card__description" style={{ margin: '0.25rem 0 0 0', fontSize: '0.9rem' }}>{milestone.description}</p>
                </div>
                <span className={`milestone-card__status milestone-card__status--${
                  milestone.status === 'Выполнено' ? 'done' : 
                  milestone.status === 'В процессе' ? 'progress' : 
                  'planned'
                }`}>
                  {milestone.status}
                </span>
              </>
            ) : (
              <>
                <h3 className="milestone-card__title">{milestone.title}</h3>
                <span className={`milestone-card__status milestone-card__status--${
                  milestone.status === 'Выполнено' ? 'done' : 
                  milestone.status === 'В процессе' ? 'progress' : 
                  'planned'
                }`}>
                  {milestone.status}
                </span>
              </>
            )}
          </div>
        </div>
        {variant !== 'current' && <p className="milestone-card__description">{milestone.description}</p>}
        <div className="milestone-card__tasks">
          <h4 className="milestone-card__tasks-title">Ключевые задачи:</h4>
          <ul className="milestone-card__tasks-list">
            {milestone.tasks.map((task, idx) => (
              <li key={idx}>{task}</li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  return (
    <div className="background-gradient">
      <section className="section">
        <div className="container milestones-content">
        {/* Completed badges */}
        {completedMilestones.length > 0 && (
          <div className="milestones-completed">
            <div className="milestones-completed__badges">
              {completedMilestones.map(m => renderMilestoneCard(m, 'badge'))}
            </div>
          </div>
        )}

        {/* Current in-progress milestone */}
        {inProgressMilestone && (
          <div className="milestones-current">
            {renderMilestoneCard(inProgressMilestone, 'current')}
          </div>
        )}

        {/* Planned milestones grid */}
        {plannedMilestones.length > 0 && (
          <div className="milestones-planned">
            <div className="milestones-grid">
              {plannedMilestones.map(m => renderMilestoneCard(m, 'planned'))}
            </div>
          </div>
        )}
        </div>
      </section>
    </div>
  );
};

export default Milestones;
